import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { transformAuthUser, transformUserDetails } from './utils';

export interface UserState {
  uid: string | null;
  given_name: string | null;
  family_name: string | null;
  email: string | null;
  jwtToken: string | null;
  identityId: string | null;
  status: string;
  image: string | null;
  oldUserDetails: unknown;
  authorization?: string | null;
  header?: string | null;
}

const initialState: UserState = {
  uid: null,
  given_name: null,
  family_name: null,
  email: null,
  jwtToken: null,
  identityId: null,
  status: 'initial',
  image: null,
  oldUserDetails: null,
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setAuthUser(state, action: PayloadAction<Parameters<typeof transformAuthUser>[0]>) {
      const authUser = transformAuthUser(action.payload);
      if (!authUser) return initialState;
      state.uid = authUser.uid;
      state.jwtToken = authUser.jwtToken;
    },
    setGuestUser(state, action: PayloadAction<{ email: string; firstname: string; lastname: string; uid: string }>) {
      const { email, firstname, lastname, uid } = action.payload;
      state.uid = uid;
      state.given_name = firstname;
      state.family_name = lastname;
      state.email = email;
    },
    setStatus(state, action: PayloadAction<string>) {
      state.status = action.payload;
    },
    setDetails(state, action: PayloadAction<Parameters<typeof transformUserDetails>[0]>) {
      const userDetails = transformUserDetails(action.payload);
      state.image = userDetails?.image ?? null;
      state.given_name = userDetails?.firstname ?? null;
      state.family_name = userDetails?.lastname ?? null;
      state.email = userDetails?.email ?? null;
    },
    updateCurrentCredentials(state, action: PayloadAction<string | null>) {
      state.identityId = action.payload;
    },
    updateAuthorization(state, action: PayloadAction<string | null>) {
      state.authorization = action.payload;
    },
    updateHeader(state, action: PayloadAction<string | null>) {
      state.header = action.payload;
    },
    setOldUserDetails(state, action: PayloadAction<unknown>) {
      state.oldUserDetails = action.payload;
    },
  },
});

export const {
  updateCurrentCredentials,
  setStatus,
  updateAuthorization,
  updateHeader,
  setAuthUser,
  setDetails,
  setGuestUser,
  setOldUserDetails,
} = userSlice.actions;

export default userSlice.reducer;
